'use client'

import React, { memo, PropsWithChildren, useCallback, useMemo } from 'react'
import { AssetSummary, SoundsAsset } from '@/lib/assets/types'
import isEqual from 'react-fast-compare'
import { buildPreviewUrl, getAssetKey } from '@/lib/assets/assets'
import { Button, buttonVariants } from '@/components/ui/button'
import { useQuery } from '@tanstack/react-query'
import { cn, downSampleSignal, formatDuration } from '@/lib/utils'
import styles from './music-bar.module.scss'
import useAudioPlayer from '@/lib/hooks/useAudioPlayer'
import { useAudioWaveform } from '@/lib/hooks/use-audio-waveform'
import { ArrowDownToLineIcon, HeartIcon, LoaderCircleIcon, PauseIcon, PlayIcon } from 'lucide-react'
import SoundWave from '@/components/assets-explorer/sound-wave'
import { features } from '@/lib/config'
import useFavorites from '@/lib/hooks/useFavorites'
import { HeartFilledIcon, StarFilledIcon } from '@radix-ui/react-icons'
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card'
import { Skeleton } from '@/components/ui/skeleton'
import { useUserContext } from '@/lib/contexts/UserContext'
import Link from 'next/link'
import useAsset, { DownloadOption } from '@/lib/hooks/useAsset'
import { forDetails } from '@/lib/assets/repositories/asset-repository-factory'
import { useUserRealtimeContext } from '@/lib/contexts/UserRealtimeContext'
import { MarketplaceAssetPurchaseContextItem } from '@/lib/marketplace/types'
import { MarketplaceAssetPurchaseButton } from '@/components/marketplace/marketplace-asset-purchase-button'
import { canDownloadAssetDirectly } from '@/lib/marketplace/asset-action-visibility'

export type MusicBarProps = {
  asset: AssetSummary
  className?: string
  hidePlaybackControls?: boolean
  hideTitle?: boolean
  hideTools?: boolean
  gridLayout?: boolean
  hideDownload?: boolean
  platform?: 'web' | 'portal'
  onClick?: (asset?: AssetSummary) => void
  marketplaceContext?: MarketplaceAssetPurchaseContextItem | null
  keyboardFocused?: boolean
  ignorePointerHover?: boolean
  keyboardTabIndex?: number
  onKeyboardFocus?: () => void
  onKeyboardBlur?: () => void
  gridRow?: number
  gridColumn?: number
}

const MusicBar = memo(
  ({
    asset,
    children,
    className,
    hidePlaybackControls = false,
    hideTitle = false,
    hideTools = false,
    gridLayout = false,
    hideDownload = false,
    platform = 'web',
    marketplaceContext,
    onClick: onOpenAsset,
    keyboardFocused = false,
    ignorePointerHover = false,
    keyboardTabIndex = -1,
    onKeyboardFocus,
    onKeyboardBlur,
    gridRow,
    gridColumn,
  }: PropsWithChildren<MusicBarProps>) => {
    const assetKey = getAssetKey(asset)

    const { data: assetDetails, isLoading: isLoadingAssetDetails } = useQuery({
      queryKey: ['asset', assetKey],
      queryFn: async () => {
        const repository = forDetails()
        return (await repository.getAssetByIdOrSlug({
          assetIdOrSlug: asset.uuid,
          type: 'sc_sounds',
          mainCategoryIdOrSlug: asset.mainCategorySlug!,
        })) as SoundsAsset
      },
      enabled: asset && !!asset.mainCategorySlug,
      initialData: asset.vertical === 'sounds' ? (asset as SoundsAsset) : undefined,
      staleTime: 30 * 1000,
    })
    const resolvedAsset = assetDetails ?? asset

    const duration = useMemo(() => {
      const seconds = resolvedAsset.properties?.seconds
      return typeof seconds === 'number' ? seconds : Number(seconds) || 0
    }, [resolvedAsset.properties?.seconds])

    const formattedDuration = useMemo(() => {
      // Duration should show at least 1 second
      return formatDuration(Math.max(duration, 1))
    }, [duration])

    const bpm = useMemo(() => {
      const value = resolvedAsset.properties?.bpm
      return typeof value === 'number' ? value : Number(value) || 0
    }, [resolvedAsset.properties?.bpm])

    const audioMetadata = [resolvedAsset.properties?.mood, resolvedAsset.properties?.category_legacy]
      .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
      .map((value) => value.trim())
      .join(' / ')

    // Disabled for now
    const assetCategory = useMemo(() => {
      // return assetDetails?.properties?.type
      return undefined
    }, [])

    const { data: normalizedWaveData } = useAudioWaveform(resolvedAsset)

    const waveData = useMemo(() => {
      if (!normalizedWaveData?.length) {
        return []
      }

      return downSampleSignal(normalizedWaveData, gridLayout ? 4 : 2)
    }, [gridLayout, normalizedWaveData])

    const audioSrc = useMemo(() => {
      if (!resolvedAsset) {
        return null
      }

      const preview = resolvedAsset.previews?.find((p) => p.type === 'audio' && p.variant === 'mp3')

      if (!preview) {
        return null
      }

      return buildPreviewUrl(preview)
    }, [resolvedAsset])

    const { isPlaying, play, stop, seek, currentTime } = useAudioPlayer(audioSrc)

    const onSeek = useCallback(
      (time: number) => {
        if (!isPlaying) {
          play(time)
        } else {
          seek(time)
        }
      },
      [isPlaying, play, seek]
    )

    const handleToggleAudio = useCallback(() => {
      if (!isPlaying) {
        play()
      } else {
        stop()
      }
    }, [isPlaying, play, stop])

    const { addFavorite, removeFavorite, isFavorite: isFavoriteFn } = useFavorites()
    const isFavorite = isFavoriteFn(assetKey)

    const { isAuthenticating, userToken, hasProFeature } = useUserContext()
    const { downloadOptions, assetDetailUrl, isDownloading, download } = useAsset({
      asset: resolvedAsset,
      userToken,
    })
    const primaryDownloadOption = downloadOptions[0]
    const canDownloadDirectly = canDownloadAssetDirectly({
      asset: assetDetails ?? asset,
      context: marketplaceContext,
      file: primaryDownloadOption?.file,
      userCanDownloadPremiumAssets: hasProFeature('assets-download'),
    })

    // Determine if portal download is possible
    const { state: userStatus, download_method: downloadMethod } = useUserRealtimeContext()
    const isPortalDownload = userStatus === 'online' && downloadMethod === 'portal'

    const handleDownload = useCallback(
      (option: DownloadOption) => {
        if (isDownloading) {
          console.warn('Download already in progress')
          return
        }

        download(option.file, {
          platform: isPortalDownload ? 'portal' : 'web',
        })
      },
      [download, isDownloading, isPortalDownload]
    )

    return (
      <div
        data-preview-audio-key={getAssetKey(asset)}
        data-grid-asset-key={getAssetKey(asset)}
        data-grid-row={gridRow}
        data-grid-column={gridColumn}
        data-ignore-pointer-hover={ignorePointerHover}
        aria-label={`Asset: ${asset.name}`}
        aria-keyshortcuts="ArrowLeft ArrowRight ArrowUp ArrowDown Space D"
        className={cn(
          styles.MusicBar,
          { [styles.KeyboardFocused]: keyboardFocused },
          {
            //'music-bar tw-grid tw-grid-flow-col tw-items-center tw-justify-start tw-gap-2 ': !gridLayout,
            'music-bar tw-flex tw-h-16 tw-items-center tw-justify-start tw-gap-2': !gridLayout,
            [styles.GridLayout]: gridLayout,
          },
          className
        )}
        tabIndex={keyboardTabIndex}
        onFocus={(event) => {
          // Focus also bubbles up from the portaled download panel; a mouse click there is not keyboard navigation.
          if (event.currentTarget.contains(event.target)) onKeyboardFocus?.()
        }}
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node | null)) onKeyboardBlur?.()
        }}
        onKeyDown={(event) => {
          if (event.target !== event.currentTarget || event.defaultPrevented) return
          if (event.key === ' ' || event.key === 'Spacebar') {
            event.preventDefault()
            onOpenAsset?.(asset)
            return
          }
          if (event.key.toLowerCase() !== 'd' || event.ctrlKey || event.metaKey || event.altKey || event.shiftKey)
            return
          event.preventDefault()
          if (canDownloadDirectly && !isDownloading && primaryDownloadOption) {
            handleDownload(primaryDownloadOption)
          }
        }}
      >
        {/* Download button */}
        {!hideTools && !gridLayout && (
          <>
            {isDownloading && <LoaderCircleIcon className="tw-h-4 tw-w-4 tw-animate-spin" />}
            {!canDownloadDirectly && (
              <MarketplaceAssetPurchaseButton asset={assetDetails ?? asset} context={marketplaceContext} compact />
            )}
            {canDownloadDirectly && !isDownloading && (
              <HoverCard openDelay={0} closeDelay={100}>
                <HoverCardTrigger asChild>
                  <Link
                    href={primaryDownloadOption?.url ?? '#'}
                    title="Download"
                    className={cn(
                      buttonVariants({
                        variant: 'transparent',
                        size: 'icon',
                      }),
                      '!tw-p-1'
                    )}
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      if (primaryDownloadOption) {
                        handleDownload(primaryDownloadOption)
                      }
                    }}
                  >
                    <ArrowDownToLineIcon className="tw-h-4 tw-w-4" />
                  </Link>
                </HoverCardTrigger>
                <HoverCardContent
                  className="tw-w-auto tw-max-w-min tw-border-0 !tw-bg-[var(--Primary)] tw-animate-in tw-fade-in"
                  side={'top'}
                  sideOffset={-1}
                  align={'center'}
                >
                  <div className="tw-flex tw-flex-col tw-items-start tw-space-y-2">
                    {(isLoadingAssetDetails || isAuthenticating) && (
                      <>
                        <Skeleton className="tw-h-4 tw-w-16" />
                        <Skeleton className="tw-h-4 tw-w-16" />
                        <Skeleton className="tw-h-4 tw-w-16" />
                      </>
                    )}
                    {!isLoadingAssetDetails && !isAuthenticating && (
                      <>
                        {downloadOptions.map((option) => (
                          <Link
                            key={`download-option-${option.url}`}
                            href={option.url}
                            prefetch={false}
                            className="tw-opacity-50 hover:tw-opacity-100"
                            onClick={(e) => {
                              e.preventDefault()
                              e.stopPropagation()
                              handleDownload(option)
                            }}
                          >
                            <div className="tw-flex tw-items-center tw-space-x-2">
                              <span className="tw-whitespace-nowrap">{option.format?.label}</span>
                              {+(option.level ?? 0) > 0 && <StarFilledIcon className={styles.starIcon} />}
                            </div>
                          </Link>
                        ))}
                      </>
                    )}
                  </div>
                </HoverCardContent>
              </HoverCard>
            )}
          </>
        )}
        <div
          className={cn(
            styles.Title,
            'tw-flex tw-w-full tw-items-center tw-gap-2',
            gridLayout ? 'tw-max-w-full' : 'tw-mr-auto tw-max-w-80'
          )}
        >
          {!hidePlaybackControls && (
            <Button
              type="button"
              title={isPlaying ? 'Pause' : 'Play'}
              size="icon"
              variant="ghost"
              className="!tw-bg-transparent hover:!tw-bg-transparent hover:!tw-text-[var(--Primary-100)]"
              disabled={!audioSrc}
              onClick={(event) => {
                event.preventDefault()
                event.stopPropagation()
                handleToggleAudio()
              }}
            >
              {isPlaying && (
                <PauseIcon
                  fill="var(--Primary-100)"
                  className="tw-h-4 tw-w-4"
                  style={{ color: 'var(--Primary-100)' }}
                />
              )}
              {!isPlaying && (
                <PlayIcon fill="var(--Primary-100)" className="tw-h-4 tw-w-4" style={{ color: 'var(--Primary-100)' }} />
              )}
            </Button>
          )}
          {!gridLayout && !!resolvedAsset.level && +resolvedAsset.level > 0 && (
            <StarFilledIcon className="tw-h-4 tw-w-4 tw-text-[var(--Secondary-200)]" />
          )}
          {!hideTitle && (
            <Link
              onClick={(event) => {
                event.stopPropagation()
                if (onOpenAsset && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
                  event.preventDefault()
                  onOpenAsset(asset)
                }
              }}
              href={assetDetailUrl ?? '#'}
              target="_self"
              className="tw-min-w-0 tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap tw-font-medium hover:tw-underline"
            >
              {asset.name}
            </Link>
          )}
        </div>
        {!gridLayout && (
          <button
            type="button"
            title={isPlaying ? `Pause ${asset.name}` : `Play ${asset.name}`}
            aria-label={isPlaying ? `Pause ${asset.name}` : `Play ${asset.name}`}
            className="tw-flex tw-h-full tw-min-w-0 tw-flex-1 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-sm tw-px-4 tw-outline-none hover:tw-bg-[var(--Primary-400)] focus-visible:tw-ring-2 focus-visible:tw-ring-[var(--Secondary)] disabled:tw-cursor-not-allowed disabled:hover:tw-bg-transparent"
            disabled={!audioSrc}
            onClick={handleToggleAudio}
          >
            {audioMetadata && (
              <span
                title={audioMetadata}
                className="tw-max-w-full tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap tw-text-[var(--Primary-300)]"
              >
                {audioMetadata}
              </span>
            )}
          </button>
        )}
        {gridLayout && (
          <div className="tw-flex tw-w-full tw-flex-row tw-items-center tw-justify-end tw-gap-2">
            <div className="tw-text-[var(--Primary-300)]">{formattedDuration}</div>
            {bpm > 0 && <div className="tw-text-[var(--Primary-300)]">{bpm} bpm</div>}
          </div>
        )}
        {!gridLayout && (
          <>
            <div className="tw-min-w-16 tw-text-[var(--Primary-300)]">{bpm ? `${bpm} bpm` : ''}</div>
            <div className="tw-text-[var(--Primary-300)]">{formattedDuration}</div>
          </>
        )}
        {gridLayout && (
          <SoundWave
            waveData={waveData ?? []}
            currentTime={isPlaying ? currentTime : 0}
            duration={duration}
            onSeek={onSeek}
            className="tw-mb-4 tw-mt-4"
          />
        )}
        {!gridLayout && (
          <SoundWave
            waveData={waveData ?? []}
            currentTime={isPlaying ? currentTime : 0}
            duration={duration}
            onSeek={onSeek}
            className="tw-mr-4"
          />
        )}
        {gridLayout && (
          <div className="tw-flex tw-w-full tw-flex-row tw-items-center tw-gap-3">
            {assetCategory && <div className="tw-mr-auto tw-text-[var(--Primary-300)]">{assetCategory}</div>}
            <div className="tw-ml-auto tw-flex tw-items-center tw-gap-2">
              <div className={cn(styles.Controls, 'tw-space-x-1')}>
                {children}
                {/* Favorite button */}
                {/* eslint-disable-next-line react/jsx-no-undef */}
                {features.favorites && (
                  <Button
                    title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                    variant="transparent"
                    size="icon"
                    className="!tw-p-1"
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      isFavorite ? removeFavorite(assetKey) : addFavorite(assetKey)
                    }}
                  >
                    {isFavorite ? (
                      <HeartFilledIcon className="tw-h-4 tw-w-4" />
                    ) : (
                      <HeartIcon className="tw-h-4 tw-w-4" />
                    )}
                  </Button>
                )}
              </div>
              {/* Download button */}
              {gridLayout && !hideDownload && (
                <>
                  {isDownloading && <LoaderCircleIcon className="tw-h-4 tw-w-4 tw-animate-spin" />}
                  {!canDownloadDirectly && (
                    <MarketplaceAssetPurchaseButton
                      asset={assetDetails ?? asset}
                      context={marketplaceContext}
                      compact
                    />
                  )}
                  {canDownloadDirectly && !isDownloading && (
                    <HoverCard openDelay={0} closeDelay={100}>
                      <HoverCardTrigger asChild>
                        <Link
                          href={primaryDownloadOption?.url ?? '#'}
                          title="Download"
                          className={cn(
                            buttonVariants({
                              variant: 'transparent',
                              size: 'icon',
                            }),
                            'tw-flex tw-items-center tw-justify-center tw-gap-1 !tw-p-1 tw-animate-in tw-fade-in'
                          )}
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            if (primaryDownloadOption) {
                              handleDownload(primaryDownloadOption)
                            }
                          }}
                        >
                          <ArrowDownToLineIcon className="tw-h-4 tw-w-4" />
                          {gridLayout && !!resolvedAsset.level && +resolvedAsset.level > 0 && (
                            <StarFilledIcon className="tw-h-4 tw-w-4 tw-text-[var(--Secondary-200)]" />
                          )}
                        </Link>
                      </HoverCardTrigger>
                      <HoverCardContent
                        className={`${gridLayout ? 'tw-max-w-prose' : 'tw-max-w-min'} tw-border-0 !tw-bg-[var(--Primary)]`}
                        side={'top'}
                        sideOffset={-1}
                        align={'start'}
                        alignOffset={!gridLayout ? -16 : 16}
                      >
                        <div className="tw-flex tw-flex-col tw-items-start tw-space-y-2">
                          {(isLoadingAssetDetails || isAuthenticating) && (
                            <>
                              <Skeleton className="tw-h-4 tw-w-16" />
                              <Skeleton className="tw-h-4 tw-w-16" />
                              <Skeleton className="tw-h-4 tw-w-16" />
                            </>
                          )}
                          {!isLoadingAssetDetails && !isAuthenticating && (
                            <>
                              {downloadOptions.map((option) => (
                                <Link
                                  key={`download-option-${option.url}`}
                                  href={option.url}
                                  prefetch={false}
                                  className="tw-opacity-50 hover:tw-opacity-100"
                                  onClick={(e) => {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    handleDownload(option)
                                  }}
                                >
                                  <div className="tw-flex tw-items-center tw-space-x-2">
                                    <span className="tw-whitespace-nowrap">{option.format?.label}</span>
                                  </div>
                                </Link>
                              ))}
                            </>
                          )}
                        </div>
                      </HoverCardContent>
                    </HoverCard>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    )
  },
  isEqual
)

MusicBar.displayName = 'MusicBar'

export { MusicBar }
